import { Avatar } from "@material-ui/core";
import { stringAvatar } from "utils/contacts/display";

/**
 * A colleague's face on the permissions screen: their photo when the org directory has
 * one, deterministic tinted initials when it does not.
 *
 * ONE COMPONENT because both places that show a person here have to make the same
 * choices — the roster row and the solo-agency card — and an avatar that fell back
 * differently in the two would read as two different people. The fallback is the app's
 * existing `stringAvatar` hash, the same one the contacts and calls tables use, so a
 * colleague keeps the same colour across the product; that consistency is most of what
 * makes an initials avatar recognisable at all.
 *
 * THE PHOTO IS NEVER WAITED FOR. The directory loads separately from the overview that
 * carries the roster, so `avatarUrl` is routinely absent on the first paint and arrives
 * (or does not) a moment later. `Avatar` swaps `src` in over the children on its own and
 * falls back to them again if the URL 404s, so both the pending and the broken case land
 * on the same readable initials rather than on a hole where a face goes.
 */
export default function CrmMemberAvatar({
  name,
  avatarUrl,
  size = 44
}: {
  name: string;
  avatarUrl: string | null | undefined;
  size?: number;
}) {
  const { sx: tint, children: initials } = stringAvatar(name);

  return (
    <Avatar
      src={avatarUrl ?? undefined}
      alt={name}
      sx={{ width: size, height: size, flexShrink: 0, color: "common.white", ...tint }}
    >
      {initials}
    </Avatar>
  );
}
